// File: layouts/MainLayout.tsx

import { useRef, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import GlobalTopBar from "@/components/GlobalTopBar";
import LicenseBanner from "@/components/LicenseBanner";
import { useSidebar } from "@/context/SidebarContext";
import { cn } from "@/lib/utils";

export default function MainLayout() {
    const { isInitialized, collapsed } = useSidebar();
    const topBarRef = useRef<HTMLDivElement>(null);

    // Measure GlobalTopBar height and set as CSS variable
    // PatientDetailPage uses var(--topbar-height) for sticky positioning
    useEffect(() => {
        const updateHeight = () => {
            if (topBarRef.current) {
                const h = topBarRef.current.getBoundingClientRect().height;
                document.documentElement.style.setProperty("--topbar-height", `${h}px`);
            }
        };
        updateHeight();
        const ro = new ResizeObserver(updateHeight);
        if (topBarRef.current) ro.observe(topBarRef.current);
        return () => ro.disconnect();
    }, []);

    if (!isInitialized) {
        return (
            <div className="min-h-screen bg-background text-foreground">
                <div className="sticky top-0 z-30 w-full" ref={topBarRef}>
                    <GlobalTopBar />
                    <LicenseBanner />
                </div>
                <div className="fixed left-0 w-12 bg-card border-r border-border z-40"
                     style={{ top: "var(--topbar-height, 60px)", height: "calc(100vh - var(--topbar-height, 60px))" }} />
                <div className="ml-12 transition-all duration-200 ease-in-out"
                     style={{ paddingTop: "var(--topbar-height, 60px)" }}>
                    <main className="bg-background">
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* GlobalTopBar — measured via ref */}
            <div className="sticky top-0 z-30 w-full" ref={topBarRef}>
                <GlobalTopBar />
                <LicenseBanner />
            </div>

            {/* Sidebar */}
            <div className="fixed left-0 h-[calc(100vh-var(--topbar-height,60px))] z-40"
                 style={{ top: "var(--topbar-height, 60px)" }}>
                <Sidebar />
            </div>

            {/* Main content — margin always matches the sidebar's actual rendered width,
                so expanded content is pushed, never covered */}
            <div className={cn(
                "transition-all duration-200 ease-in-out",
                collapsed ? "ml-12" : "ml-44 lg:ml-[180px] xl:ml-[200px] 2xl:ml-[210px]"
            )}>
                <main className="bg-background p-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}